import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const now = new Date();
const whatsappLink = (fee) => {
  const phone = String(fee.student?.contactNo || '').replace(/\D/g, '').replace(/^0/, '92');
  const total = Number(fee.amount || 0) + Number(fee.fine || 0);
  const message = `Assalam-o-Alaikum ${fee.student?.name || ''}, your Royal Tower hostel fee for ${fee.monthLabel} is PKR ${total.toLocaleString()}. Current status: ${fee.status}. Please contact the administration if you need assistance.`;
  return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : '';
};

function WhatsAppIcon() {
  return <svg className="fee-whatsapp-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor" /><path d="M8.1 7.5c.3-.5.6-.5 1-.5h.3c.2 0 .4.1.5.4l1 2.2c.1.3.1.5-.1.8l-.7.9c-.2.2-.2.4-.1.6.7 1.4 1.8 2.5 3.2 3.2.2.1.4.1.6-.1l.9-1.1c.2-.3.5-.3.8-.2l2.1 1c.3.1.4.3.4.6 0 .6-.3 1.7-1 2.2-.6.5-1.5.8-2.5.6-1.4-.3-3.2-1-5.1-2.8-1.5-1.5-2.5-3.2-2.8-4.6-.3-1.2.1-2.3.5-2.9l1-.3Z" fill="#fff" /></svg>;
}

export default function Fees() {
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [filterStatus, setFilterStatus] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [form, setForm] = useState({ student: '', month: now.getMonth() + 1, year: now.getFullYear(), amount: '', fine: 0, status: 'Unpaid', notes: '' });
  const [msg, setMsg] = useState('');
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [feesRes, studRes] = await Promise.all([
        api.get('/fees', { params: { month: filterMonth, year: filterYear, status: filterStatus || undefined } }),
        api.get('/students', { params: { status: 'Active' } })
      ]);
      setFees(feesRes.data);
      setStudents(studRes.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterMonth, filterYear, filterStatus]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/fees', form);
      setShowModal(false);
      setMsg('Fee record added!');
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Error');
    }
  };

  const toggleStatus = async (fee) => {
    const newStatus = fee.status === 'Paid' ? 'Unpaid' : 'Paid';
    const paidDate = newStatus === 'Paid' ? new Date().toISOString().split('T')[0] : null;
    await api.put(`/fees/${fee._id}`, { status: newStatus, paidDate });
    load();
  };

  const deleteFee = async (id) => {
    if (!window.confirm('Delete this fee record?')) return;
    await api.delete(`/fees/${id}`);
    load();
  };

  const generateFees = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/fees/generate', { month: filterMonth, year: filterYear });
      setMsg(res.data.message);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Error generating fees');
    } finally { setGenerating(false); }
  };

  const searchText = studentSearch.trim().toLowerCase();
  const filteredFees = fees.filter(f => {
    if (!searchText) return true;
    const student = f.student || {};
    return [student.name, student.room, student.contactNo, student.cnic]
      .some(value => String(value || '').toLowerCase().includes(searchText));
  });
  const totalPaid = filteredFees.filter(f => f.status === 'Paid').reduce((s, f) => s + f.amount, 0);
  const totalUnpaid = filteredFees.filter(f => f.status === 'Unpaid').reduce((s, f) => s + f.amount, 0);

  return (
    <Layout title="Fee Management">
      {msg && <div className="alert alert-info">{msg} <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setMsg('')}>✕</button></div>}

      {/* Summary */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', maxWidth: 500 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#38a169', color: '#fff' }}>✅</div>
          <div className="stat-info"><h3 style={{ fontSize: 16 }}>PKR {totalPaid.toLocaleString()}</h3><p>Collected</p></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e53e3e', color: '#fff' }}>⚠️</div>
          <div className="stat-info"><h3 style={{ fontSize: 16 }}>PKR {totalUnpaid.toLocaleString()}</h3><p>Pending</p></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#3182ce', color: '#fff' }}>📋</div>
          <div className="stat-info"><h3>{filteredFees.length}</h3><p>Records</p></div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">💰 Fee Records</h3>
          <div className="flex gap-10">
            <button className="btn btn-success btn-sm" onClick={generateFees} disabled={generating}>
              {generating ? '...' : '⚡ Auto-Generate'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>➕ Add Fee</button>
          </div>
        </div>

        <div className="filters-bar">
          <input
            type="search"
            value={studentSearch}
            onChange={e => setStudentSearch(e.target.value)}
            placeholder="Search student, room, contact or CNIC..."
            aria-label="Search fee records by student"
            style={{ minWidth: 280, flex: '1 1 300px' }}
          />
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option>Paid</option>
            <option>Unpaid</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center" style={{ padding: 40 }}>Loading...</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Room</th>
                  <th>Month</th>
                  <th>Amount</th>
                  <th>Fine</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Paid Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFees.length === 0 ? (
                  <tr><td colSpan={9} className="text-center" style={{ padding: 30, color: '#718096' }}>{studentSearch ? 'No matching student fee records found.' : 'No records. Use "Auto-Generate" to create monthly fees.'}</td></tr>
                ) : filteredFees.map(f => (
                  <tr key={f._id}>
                    <td><strong>{f.student?.name || 'N/A'}</strong></td>
                    <td><span className="badge badge-info">{f.student?.room}</span></td>
                    <td>{f.monthLabel}</td>
                    <td>PKR {f.amount.toLocaleString()}</td>
                    <td>{f.fine > 0 ? `PKR ${f.fine.toLocaleString()}` : '-'}</td>
                    <td><strong>PKR {(f.amount + f.fine).toLocaleString()}</strong></td>
                    <td>
                      <span className={`badge ${f.status === 'Paid' ? 'badge-success' : 'badge-danger'}`}>
                        {f.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>{f.paidDate ? f.paidDate.split('T')[0] : '-'}</td>
                    <td>
                      <div className="flex gap-10">
                        {f.student?.contactNo && <a className="btn btn-sm fee-whatsapp-btn" href={whatsappLink(f)} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${f.student.name}`} title={`WhatsApp ${f.student.name}`}><WhatsAppIcon /> WhatsApp</a>}
                        <button
                          className={`btn btn-sm ${f.status === 'Paid' ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => toggleStatus(f)}
                        >
                          {f.status === 'Paid' ? '↩ Unpaid' : '✅ Mark Paid'}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteFee(f._id)}>🗑 Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Fee Record</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAdd}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Student *</label>
                    <select value={form.student} onChange={e => {
                      const s = students.find(st => st._id === e.target.value);
                      setForm(f => ({ ...f, student: e.target.value, amount: s?.monthlyFee || '' }));
                    }} required>
                      <option value="">-- Select Student --</option>
                      {students.map(s => <option key={s._id} value={s._id}>{s.name} ({s.room})</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Month *</label>
                    <select value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))} required>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Year *</label>
                    <select value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}>
                      {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Amount (PKR) *</label>
                    <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required min="0" />
                  </div>
                  <div className="form-group">
                    <label>Fine (PKR)</label>
                    <input type="number" value={form.fine} onChange={e => setForm(f => ({ ...f, fine: e.target.value }))} min="0" />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      <option>Unpaid</option>
                      <option>Paid</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Notes</label>
                    <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">✅ Add Fee</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
